import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, withStyles } from '@material-ui/core';
import ActionButton from 'components/shared/actionButton';
import { ACTION_BUTTON_TYPES } from 'components/shared/actionButton/config';
import Search from '../../shared/search';
import GridTable from '../../shared/gridTable';
import ManageActionPlan from '../../shared/manageActionPlan';
import CustomButton from '../../shared/customButton';
import AlertDialog from '../../shared/alertDialog';
import ReadLessMore from '../../shared/readLessMore';
import UserAvatar from '../../shared/userAvatar';
import Filters from '../../shared/filters';
import StatusIndicator from '../../shared/statusIndicator';
import NotificationCard from '../../shared/notificationCard';
import DotsMenu from '../../shared/dotsMenu';
import ManageTasks from '../../shared/manageTasks';
import TasksIconTooltip from '../../shared/tasksIconTooltip';
import CoursesIconTooltip from '../../shared/coursesIconTooltip';
import ConditionalTooltip from '../../shared/conditionalTooltip';
import RequestedIconTooltip from '../../shared/requestedIconTooltip';
import ProposedStatus from '../../shared/proposedStatus';
import {
  PARAMS,
  PAGE_WHITELISTED_PARAMS,
  ACTION_PLANS_DEFAULT_PARAMS,
} from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import {
  api_action_plan,
  api_action_plan_tasks,
} from '../../../constants/apiRoutes';
import { GENERAL_CATEGORY_ID } from '../../../constants/tags';
import { sticky } from '../../../constants/helperCssRules';
import {
  showSuccessMessage,
  parseQueryParams,
  parseDuplicateParameters,
  hasSelectedFiltersCheck,
} from '../../../utility/uiUtils';
import { onSaveCreatableTag } from '../../../utility/tagUtils';
import {
  isArrayEmpty,
  trimString,
  isArray,
  getItemById,
  isObjectEmpty,
  isUserDeactivated,
} from '../../../utility/helpers';
import { prepareTasksForSave } from '../../../utility/tasks';
import http from '../../../utility/http';
import {
  prepareTranslatedActionPlanStatuses,
  prepareActionPlansPageFilters,
  prepareActionPlanFilterStatuses,
  prepareActionPlansDotsMenuItems,
} from '../../../utility/actionPlans';
import {
  hasAuthPermissionCheck,
  hasScopedAuthPermissionCheck,
  prepareUsersWithGrantedPermission,
} from '../../../utility/permissions';
import {
  INITIAL_SORT,
  getHeaderActions,
  getTableHeaders,
  prepareTableData,
} from './config';

const styles = ({ breakpoints, palette: { primary }, spacing }) => ({
  stickyHeader: {
    ...sticky(primary.white, 105),
    padding: spacing(8, 0, 6, 0),
  },
  table: {
    '& .grid-table-header': {
      ...sticky(primary.bluish8, 243),
      '&.grid-table-header$readMoreActive': {
        top: 297,
      },
    },
    '& .title-cell, & .due-date-cell, & .modified-on-cell': {
      '& .row-cell-value': {
        color: primary.bluish1,
      },
    },
    '& .created-for-cell': {
      '& .row-cell-value': {
        overflow: 'visible',
      },
    },
    '& .actions-cell': {
      overflow: 'visible',
      paddingLeft: 0,
      paddingTop: spacing(2),
      paddingBottom: spacing(2),
      paddingRight: spacing(1),

      [breakpoints.up('xLg')]: {
        paddingRight: spacing(3),
      },
    },
  },
  readMoreActive: {},
  rightSide: {
    display: 'flex',
    alignSelf: 'center',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  addAttribute: {
    width: 'max-content',
  },
  search: {
    width: 234,
    marginRight: spacing(2),
  },
  pageDescription: {
    marginBottom: spacing(6),
  },
  filtersSection: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    marginRight: spacing(2),
  },
  userWrapper: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    maxWidth: '100%',
  },
  user: {
    maxWidth: 'calc(100% - 30px)',
  },
  userLabel: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 16,
    lineHeight: '20px',
  },
  statusLabel: {
    padding: spacing(1, 2),
    fontSize: 10,
    lineHeight: '12px',
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  todo: {
    width: 31,
    background: primary.bluish8,
    color: primary.blue1,
  },
  done: {
    width: 31,
    background: primary.bluish1,
    color: primary.white,
  },
  inProgress: {
    width: 65,
    background: primary.orange,
    color: primary.white,
  },
  statusWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  iconTooltip: {
    cursor: 'pointer',
    marginLeft: spacing(1.5),
  },
  icon: {
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  acceptButton: {
    marginRight: spacing(2),
  },
});

const { PAGE, TAGS, SEARCH, ORDERING, COMPLETION_STATUS, USER, DUE_DATE } =
  PARAMS;

class ActionPlansPage extends PureComponent {
  state = {
    isInitialLoad: true,
    isLoading: false,
    [PAGE]: 1,
    [SEARCH]: '',
    filters: {
      [COMPLETION_STATUS]: [],
      [TAGS]: [],
      [USER]: [],
      [DUE_DATE]: [],
      ...(this.props.location?.state?.filters
        ? this.props.location.state.filters
        : {}),
    },
    [ORDERING]: INITIAL_SORT,
    isReadMore: true,
    isManagePlanOpened: false,
    isDeleteOpened: false,
    isDismissRequestOpened: false,
    isManageTasksOpened: false,
    actionPlan: {},
  };

  componentDidMount() {
    this.getInitialData();
  }

  componentDidUpdate() {
    const { location, navigate } = this.props;
    const { page, filters, ordering, search } = this.state;

    const params = {
      page,
      [ORDERING]: ordering.sortKey,
      ...filters,
      ...(search ? { search } : {}),
    };
    const query = parseDuplicateParameters(params);

    if (decodeURIComponent(location.search) !== `?${query}`) {
      navigate(`/action-plans/?${query}`, { replace: true });
    }
  }

  componentWillUnmount() {
    const {
      clearAllActionPlans,
      clearTagsCategories,
      clearAllUsers,
      clearPageQuickFilters,
      clearCourses,
      clearCourseEnrolledUsers,
    } = this.props;

    clearAllActionPlans();
    clearTagsCategories();
    clearAllUsers();
    clearPageQuickFilters();
    clearCourses();
    clearCourseEnrolledUsers();
  }

  getInitialData = () => {
    const {
      location,
      translations,
      getPageQuickFilters,
      getTagsCategories,
      getAllUsers,
    } = this.props;
    const columns = getTableHeaders(translations.columns);
    const params = parseQueryParams(
      location.search,
      PAGE_WHITELISTED_PARAMS.ACTION_PLANS
    );

    if (!isObjectEmpty(params)) {
      const [ascending, descending] = params[ORDERING].split('-');
      const isAscending = !!ascending;

      return this.setState(
        {
          [ORDERING]: {
            column: getItemById(
              columns,
              isAscending ? ascending : descending,
              'sortAs'
            )?.rowKey,
            asc: isAscending,
            sortKey: params[ORDERING],
          },
          [SEARCH]: params[SEARCH] ? decodeURIComponent(params[SEARCH]) : '',
          filters: {
            [COMPLETION_STATUS]: params[COMPLETION_STATUS]
              ? [params[COMPLETION_STATUS]]
              : [],
            [TAGS]: params[TAGS]
              ? [...(isArray(params[TAGS]) ? params[TAGS] : [params[TAGS]])]
              : [],
            [USER]: params[USER]
              ? [...(isArray(params[USER]) ? params[USER] : [params[USER]])]
              : [],
            [DUE_DATE]: params[DUE_DATE] ? [params[DUE_DATE]] : [],
          },
        },
        () => {
          return Promise.all([
            this.handlePageChange(false),
            getTagsCategories(),
            getPageQuickFilters(),
            getAllUsers(),
          ]).then(() => {
            this.setState({ isInitialLoad: false });
          });
        }
      );
    }

    return Promise.all([
      this.handlePageChange(),
      getTagsCategories(),
      getPageQuickFilters(),
      getAllUsers(),
    ]).then(() => {
      this.setState({ isInitialLoad: false });
    });
  };

  handlePageChange = (isLoadMore = false) => {
    const { getAllActionPlans } = this.props;
    const { page, filters, ordering, search } = this.state;

    const params = {
      page,
      [ORDERING]: ordering.sortKey,
      ...filters,
      ...(search ? { search } : {}),
    };

    return getAllActionPlans(isLoadMore, params);
  };

  onSort = newOrdering => {
    this.setState(
      { [PAGE]: ACTION_PLANS_DEFAULT_PARAMS[PAGE], [ORDERING]: newOrdering },
      () => this.handlePageChange()
    );
  };

  onSearch = text => {
    const searchTerm = trimString(text);

    this.setState(
      {
        [PAGE]: ACTION_PLANS_DEFAULT_PARAMS[PAGE],
        [SEARCH]: searchTerm,
        isLoading: true,
      },
      () =>
        this.handlePageChange().then(() => this.setState({ isLoading: false }))
    );
  };

  handleApplyFilters = filters => {
    this.setState(
      { [PAGE]: ACTION_PLANS_DEFAULT_PARAMS[PAGE], filters, isLoading: true },
      () =>
        this.handlePageChange().then(() => this.setState({ isLoading: false }))
    );
  };

  onLoadMore = () => {
    return this.setState(
      prevState => ({ ...prevState, [PAGE]: prevState[PAGE] + 1 }),
      () => this.handlePageChange(true)
    );
  };

  handleOpenManagePlan = () =>
    this.setState({
      isManagePlanOpened: true,
    });

  handleEditPlan = actionPlan =>
    this.setState({
      isManagePlanOpened: true,
      actionPlan,
    });

  handleCloseManagePlan = () => {
    this.setState({
      isManagePlanOpened: false,
      isDeleteOpened: false,
      actionPlan: {},
    });
  };

  handleConfirmPlan = () =>
    this.setState({ [PAGE]: ACTION_PLANS_DEFAULT_PARAMS[PAGE] }, () =>
      this.handlePageChange()
    );

  handleDelete = actionPlan =>
    this.setState({ actionPlan, isDeleteOpened: true });

  handleOpenDelete = () => this.setState({ isDeleteOpened: true });

  handleCloseDeleteDialog = () => this.setState({ isDeleteOpened: false });

  onDelete = () => {
    const { translations } = this.props;
    const { actionPlan, isDismissRequestOpened } = this.state;
    const successMessage = isDismissRequestOpened
      ? translations.dismissActionPlanProposalDialog.deleteSuccess
      : translations.deleteActionPlanDialog.deleteSuccess;

    return http.delete(api_action_plan(actionPlan.id)).then(() => {
      this.setState(
        {
          [PAGE]: ACTION_PLANS_DEFAULT_PARAMS[PAGE],
          isManagePlanOpened: false,
          isDeleteOpened: false,
          isDismissRequestOpened: false,
          actionPlan: {},
        },
        () => {
          this.handlePageChange();
        }
      );
      showSuccessMessage(successMessage);
    }, this.handleCloseManagePlan);
  };

  handleDismissProposal = actionPlan => {
    this.setState({ actionPlan, isDismissRequestOpened: true });
  };

  handleCloseDismissDialog = () => {
    this.setState({ actionPlan: {}, isDismissRequestOpened: false });
  };

  handleOpenManageTasks = actionPlan =>
    this.setState({ isManageTasksOpened: true, actionPlan });

  handleCloseManageTasks = () =>
    this.setState({ isManageTasksOpened: false, actionPlan: {} });

  onSaveTasks = async values => {
    const { translations, auth } = this.props;
    const { actionPlan } = this.state;

    const isCreatedForCurrentUser = auth.id === actionPlan.user.id;
    const tasks = prepareTasksForSave(values.tasks);
    const data = {
      tasks,
      user: actionPlan.user.id,
    };

    if (isCreatedForCurrentUser) {
      await http.post(api_action_plan_tasks(actionPlan.id), data);
    } else {
      await http.patch(api_action_plan(actionPlan.id), data);
    }

    showSuccessMessage(translations.manageTasksDialog.successMessage);

    return this.setState(
      { [PAGE]: ACTION_PLANS_DEFAULT_PARAMS[PAGE] },
      this.handlePageChange
    );
  };

  handleToggleReadMore = () => {
    this.setState(prevState => ({
      isReadMore: !prevState.isReadMore,
    }));
  };

  onCreateTag = async tagName => {
    const { addMultipleTags } = this.props;
    const tags = await onSaveCreatableTag(tagName);
    const [tag] = tags.create;

    addMultipleTags({ categoryId: GENERAL_CATEGORY_ID, tags });

    return tag;
  };

  canManageTasksFormCheck = task => {
    const { auth, accessControl } = this.props;
    const { actionPlan } = this.state;
    const canManage = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PLANS_EDIT,
      accessControl,
      auth.id,
      actionPlan.user?.id
    );
    const isTaskCreatorCurrentUser = task.creator === auth.id;
    const isNewTask = !task.id;

    return canManage || isTaskCreatorCurrentUser || isNewTask;
  };

  handleCoursesSearch = value => {
    const { getCourses } = this.props;
    const text = trimString(value);
    const params = {
      ...(text ? { search: text } : {}),
    };

    return getCourses(params);
  };

  renderUser = (user, isProposed, isCreatedForCurrentUser) => {
    const { classes, translations } = this.props;

    return (
      <div className={classes.userWrapper}>
        <UserAvatar
          className={classNames({ [classes.user]: isProposed })}
          labelClass={classes.userLabel}
          user={user}
          caption
          small
        />
        {isProposed && (
          <RequestedIconTooltip
            className={classes.iconTooltip}
            labels={translations.proposedTooltip}
            isRequestedByCurrentUser={isCreatedForCurrentUser}
          />
        )}
      </div>
    );
  };

  renderStatus = (status, progress, hasTasks, courses, isPendingProposal) => {
    const { classes, translations, organizationSettings } = this.props;

    return (
      <div className={classes.statusWrapper}>
        {isPendingProposal ? (
          <>
            <ProposedStatus proposedLabel={translations.proposedStatus} />
            <CoursesIconTooltip
              className={classes.iconTooltip}
              text={translations.coursesIconTooltip}
              isVisible={
                organizationSettings?.users_see_courses &&
                !isArrayEmpty(courses)
              }
            />
          </>
        ) : (
          <>
            <StatusIndicator status={status} />
            <ConditionalTooltip
              className={classes.iconTooltip}
              message={translations.progressTooltip}
              addTooltip
            >
              <Typography variant="subtitle2">{`${progress}%`}</Typography>
            </ConditionalTooltip>
            <TasksIconTooltip
              className={classes.iconTooltip}
              iconClass={classes.icon}
              text={translations.tasksIconTooltip}
              isVisible={hasTasks}
            />
            <CoursesIconTooltip
              className={classes.iconTooltip}
              text={translations.coursesIconTooltip}
              isVisible={
                organizationSettings?.users_see_courses &&
                !isArrayEmpty(courses)
              }
            />
          </>
        )}
      </div>
    );
  };

  renderActions = (
    actionPlan,
    canManage,
    canDelete,
    isPendingProposal,
    isCreatedForCurrentUser
  ) => {
    const { classes, translations } = this.props;
    const isDisabled = isUserDeactivated(actionPlan.user);
    const { ACCEPT } = ACTION_BUTTON_TYPES;

    return (
      <div className={classes.actions}>
        {!isDisabled &&
          isPendingProposal &&
          !isCreatedForCurrentUser &&
          canManage && (
            <ActionButton
              className={classes.acceptButton}
              type={ACCEPT}
              tooltipText={translations.actionButtons[ACCEPT.toLowerCase()]}
              onClickHandler={() => this.handleEditPlan(actionPlan)}
              isSquared
            />
          )}
        <DotsMenu
          menuItems={prepareActionPlansDotsMenuItems(
            translations.menuItemLabels,
            actionPlan,
            canManage,
            canDelete,
            isDisabled,
            isPendingProposal,
            isCreatedForCurrentUser,
            this.handleEditPlan,
            this.handleDelete,
            this.handleOpenManageTasks,
            this.handleDismissProposal
          )}
        />
      </div>
    );
  };

  render() {
    const {
      classes,
      translations,
      actionPlans,
      organizationSettings,
      auth,
      accessControl,
      courses,
      categories,
      pageQuickFilters,
      allUsers,
      getCourses,
      getCourseEnrolledUsers,
    } = this.props;
    const {
      isInitialLoad,
      isLoading,
      ordering,
      isReadMore,
      filters,
      isManagePlanOpened,
      isDeleteOpened,
      isManageTasksOpened,
      actionPlan,
      search,
      isDismissRequestOpened,
    } = this.state;
    const canEdit = hasAuthPermissionCheck(
      PERMISSION_CODES.PLANS_EDIT,
      accessControl
    );
    const statuses = prepareTranslatedActionPlanStatuses(translations.statuses);
    const statusFilters = prepareActionPlanFilterStatuses(
      translations.statuses
    );
    const visibleUsers = prepareUsersWithGrantedPermission(
      allUsers,
      PERMISSION_CODES.PLANS_VIEW,
      accessControl,
      auth.id
    );
    const managableUsers = prepareUsersWithGrantedPermission(
      allUsers,
      PERMISSION_CODES.PLANS_EDIT,
      accessControl,
      auth.id
    );

    const { users_see_courses } = organizationSettings;

    const showLoadMore = !!actionPlans.next;

    const filterIsActive = hasSelectedFiltersCheck(filters);

    return (
      !isInitialLoad && (
        <div>
          <div className={classes.stickyHeader}>
            <div className={classes.pageDescription}>
              <ReadLessMore
                translations={translations}
                isReadMore={isReadMore}
                toggleReadMore={this.handleToggleReadMore}
              >
                <Typography variant="body2">
                  {translations.descriptionPartOne}
                </Typography>
                <Typography variant="body2">
                  {translations.descriptionPartTwo}
                </Typography>
                <Typography variant="body2">
                  {translations.descriptionPartThree}
                </Typography>
              </ReadLessMore>
            </div>
            <div className={classes.filtersSection}>
              <div className={classes.filters}>
                <Filters
                  translations={translations.filters}
                  selectedFilters={filters}
                  filters={prepareActionPlansPageFilters(
                    translations,
                    statusFilters,
                    categories,
                    pageQuickFilters,
                    visibleUsers,
                    true
                  )}
                  onApplyFilters={this.handleApplyFilters}
                />
              </div>
              <div className={classes.rightSide}>
                <Search
                  className={classes.search}
                  placeholder={translations.search}
                  value={search}
                  onChange={this.onSearch}
                />
                {canEdit && (
                  <CustomButton
                    className={classes.addAttribute}
                    type="addRoundedNew"
                    onClick={this.handleOpenManagePlan}
                  >
                    {translations.addActionPlan}
                  </CustomButton>
                )}
              </div>
            </div>
          </div>
          <div>
            {isArray(actionPlans.results) &&
              !isArrayEmpty(actionPlans.results) && (
                <GridTable
                  className={classes.table}
                  translations={translations}
                  customHeaderClass={classNames({
                    [classes.readMoreActive]: !isReadMore,
                  })}
                  headerActions={getHeaderActions(translations.columns)}
                  headers={getTableHeaders(translations.columns)}
                  initialSort={ordering}
                  hasLoadMore={showLoadMore}
                  rows={prepareTableData(
                    actionPlans.results,
                    auth,
                    accessControl,
                    statuses,
                    this.renderUser,
                    this.renderStatus,
                    this.renderActions
                  )}
                  onLoadMore={this.onLoadMore}
                  onSort={this.onSort}
                />
              )}
            {!isLoading &&
              isArray(actionPlans.results) &&
              isArrayEmpty(actionPlans.results) && (
                <NotificationCard
                  title={
                    search || filterIsActive ? '' : translations.noResultsTitle
                  }
                  content={
                    search || filterIsActive
                      ? translations.noSearchResultsMessage
                      : translations.noResultsMessage
                  }
                />
              )}
          </div>
          <ManageActionPlan
            translations={translations.manageActionPlan}
            isOpen={isManagePlanOpened}
            isInitialValid={!isObjectEmpty(actionPlan)}
            auth={auth}
            accessControl={accessControl}
            actionPlan={actionPlan}
            allEmployees={allUsers}
            managableEmployees={managableUsers}
            statuses={statuses}
            categories={categories}
            courses={courses.results}
            getCourses={getCourses}
            canSeeCourses={users_see_courses}
            getCourseEnrolledUsers={getCourseEnrolledUsers}
            onCoursesSearch={this.handleCoursesSearch}
            onCreateTag={this.onCreateTag}
            onDelete={this.handleOpenDelete}
            onClose={this.handleCloseManagePlan}
            onConfirm={this.handleConfirmPlan}
            hasCreatedForField
          />
          {actionPlan && (
            <ManageTasks
              translations={translations.manageTasksDialog}
              isOpened={isManageTasksOpened}
              record={actionPlan}
              availableUsers={allUsers}
              checkCanManageTask={this.canManageTasksFormCheck}
              onClose={this.handleCloseManageTasks}
              onSave={this.onSaveTasks}
            />
          )}
          <AlertDialog
            isOpened={isDeleteOpened}
            translations={translations.deleteActionPlanDialog}
            onClose={this.handleCloseDeleteDialog}
            onConfirm={this.onDelete}
            isWarning
          />
          <AlertDialog
            translations={translations.dismissActionPlanProposalDialog}
            isOpened={isDismissRequestOpened}
            onClose={this.handleCloseDismissDialog}
            onConfirm={this.onDelete}
            isWarning
          />
        </div>
      )
    );
  }
}

ActionPlansPage.propTypes = {
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  auth: PropTypes.shape({}).isRequired,
  accessControl: PropTypes.object.isRequired,
  actionPlans: PropTypes.object.isRequired,
  courses: PropTypes.shape({}).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  pageQuickFilters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allUsers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  organizationSettings: PropTypes.object.isRequired,
  addMultipleTags: PropTypes.func.isRequired,
  getCourses: PropTypes.func.isRequired,
  getCourseEnrolledUsers: PropTypes.func.isRequired,
  getAllActionPlans: PropTypes.func.isRequired,
  clearAllActionPlans: PropTypes.func.isRequired,
  getTagsCategories: PropTypes.func.isRequired,
  getPageQuickFilters: PropTypes.func.isRequired,
  clearPageQuickFilters: PropTypes.func.isRequired,
  clearTagsCategories: PropTypes.func.isRequired,
  clearCourses: PropTypes.func.isRequired,
  clearCourseEnrolledUsers: PropTypes.func.isRequired,
  getAllUsers: PropTypes.func.isRequired,
  clearAllUsers: PropTypes.func.isRequired,
};

export default withStyles(styles)(ActionPlansPage);
